import OrdersPage from 'pages/Orders'
import UpdatesPage from 'pages/Updates'
import * as PropTypes from 'prop-types'
import { memo } from 'react'
import OffCanvas from 'react-aria-offcanvas'
import { Badge, Button, Nav, Navbar } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import { useAccount } from 'state/Account'
import { PRIVILEGES } from 'state/Constants'
import { getIsCMO, useReseller, useResellerStyle } from 'state/Reseller'
import { useActiveRestaurant, useRestaurantsByStatus } from 'state/Restaurants'
import { getUpdatesVersion } from 'state/Settings'
import { klass, platform, SupportChat } from 'utils'
import useThirdPartyDeliveryStatus from 'utils/useThirdPartyDeliveryStatus'
import MenuItem from './MenuItem'
import RestaurantSelector from './RestaurantSelector'
import './styles.scss'

export default memo(Menu) //helps to avoid re-render of the menu items (which would trash their states)
function Menu({ isOpen, toggle }) {
	const { t } = useTranslation()

	/** Shows on the Status page to avoid confusion between the current states and the status below the RestSelector */
	const showSelector  = (useLocation().pathname.match(`(${OrdersPage.URIS}|status)`))
	const reseller      = useReseller()
	const isCMO         = useSelector(getIsCMO)
	const resellerStyle = useResellerStyle()
	const account       = useAccount()
	const supportChat   = SupportChat.use()
	const newVersions   = UpdatesPage.LATEST_VERSION[isCMO] - useSelector(getUpdatesVersion)

	const hasLogo     = account.logo && typeof account.logo['1x'] != 'undefined'
	const headerClass = klass(
		hasLogo? 'with-logo' : 'no-logo',
		reseller? (resellerStyle && resellerStyle.logo? 'reseller-logo' : 'reseller') : 'ehungry',
	)

	const { total, active, open, allClosed } = useRestaurantsByStatus()

	//third party delivery closed
	const activeRest = useActiveRestaurant()
	const hasDelivery = activeRest?.enabled_base_order_types.includes('DELIVERY') || open.some(rest => rest.enabled_base_order_types.includes('DELIVERY'))
	const {isThirdPartyDeliveryDisabled} = useThirdPartyDeliveryStatus(hasDelivery ? 'DELIVERY' : '', activeRest)

	const statusIcon = !(hasDelivery && isThirdPartyDeliveryDisabled) && ((active && open.includes(active)) || open.length == total)?
		['success', 'circle-check'] : ((active && allClosed.includes(active)) || allClosed.length == total ?
			['danger', 'octagon-xmark'] :
				['warning', 'exclamation-triangle'])

	const Item = MenuItem.withToggle(toggle)

	return (
		//if you need to change this width, don't forget to change the SCSS variable too! (at _variables.scss)
		<OffCanvas isOpen={isOpen} height="100vh" width="12.5rem" onClose={toggle} overlayClassName="menu-overlay">
			<Navbar id="menu" expand={false} {...headerClass}>
				<Button onClick={toggle} className="close" variant="link">×</Button>{/* eslint-disable-line i18next/no-literal-string */}
				<Link to="/">
					<Navbar.Brand as="header">
						{hasLogo && <img alt="Logo" src={account.logo['1x']}
						                 srcSet={Object.entries(account.logo).map(([size, url]) => `${url} ${size}`).join(',')}/>}
					</Navbar.Brand>
				</Link>

				<Nav as="ul">
					{/* Shouldn't be a link, but in case the user thinks it is, let's do something useful */}
					<Item noNav to="/" title={t('Orders:')}/>
					<Nav.Item as="li">
						<ul>
							{OrdersPage.useMapStates((key, link, icon, text, isCurrent, count) => (
								<Item key={key} to={link} title={text} {...icon}>
									<Badge variant={isCurrent? 'secondary' : 'light'}>{count || '-'}</Badge>
								</Item>
							))}
						</ul>
					</Nav.Item>

					{/* not just "Items" so it's not confused with the Order Items when visiting that screen */}
					<Item to="/items" icon="book-open" title={t('Menu items')} priv={PRIVILEGES().MENU_UPDATE}/>

					<Item to="/invoices" icon="file-invoice-dollar" title={t('Invoices')} show={!!reseller?.show_invoices} priv={PRIVILEGES().BILLING_INFO}/>

					{/* TODO LiveChat is broken in Native - see sc30357 */}
					<Item icon="comments-question" to={() => supportChat.toggle()} title={t('Support chat')} show={supportChat && !(platform.isNative() && supportChat.platformName == 'LiveChat')}/>

					<Item to="/settings" icon="cog" title={t('Settings')}/>
					<Item to="/updates"  title={t('Updates')} icon={newVersions? 'star' : ['far', 'star']} iconColor={newVersions? 'warning' : null}>
						{newVersions > 0 && <Badge variant="secondary">{newVersions}</Badge>}
					</Item>
				</Nav>

				<footer>
					<small>{account.display_name} ({account.id_string})</small>
					{showSelector && <RestaurantSelector/>}
					<Nav as="ul">
						<Item to="/status" title={t('Online ordering')} iconColor={statusIcon[0]} icon={statusIcon[1]}/>
					</Nav>
				</footer>
			</Navbar>
		</OffCanvas>
	)
}

Menu.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	toggle: PropTypes.func.isRequired,
}
